import React from 'react';
import { Icon } from '@iconify-icon/react';

const pillars = [
  {
    icon: 'tabler:database-off',
    title: 'Keep inference ephemeral',
    body: 'With AWS Bedrock, inference has zero data retention by default. Inputs and outputs are not shared with the underlying model providers for training.',
  },
  {
    icon: 'tabler:users-group',
    title: 'Govern who gets access',
    body: 'Use authenticated private agents, role-based access controls, and domain restrictions to put approved knowledge in front of the right audiences.',
  },
  {
    icon: 'tabler:quote',
    title: 'Prove every important answer',
    body: 'Ground agents in the sources you approve and return citations with answers, so teams can verify the evidence instead of trusting an untraceable response.',
  },
];

const safeguards = [
  { icon: 'tabler:lock', text: 'Encrypted at rest and in transit' },
  { icon: 'tabler:adjustments-shield', text: 'Role-based access controls' },
  { icon: 'tabler:user-shield', text: 'Authenticated private agents' },
  { icon: 'tabler:file-search', text: 'Security logging and oversight' },
];

const PrivateByDesign: React.FC = () => (
  <section aria-label="How Chat.co keeps your data private" className="bg-white pb-20 sm:pb-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-[28px] bg-[#04182e] px-6 py-14 sm:px-12">
        {/* Texture: grid + orbs, matching TrustBar */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff07_1px,transparent_1px),linear-gradient(to_bottom,#ffffff07_1px,transparent_1px)] bg-[size:3rem_3rem]" />
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary-600/30 blur-[90px]" />
          <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-[#d3a641]/10 blur-[90px]" />
        </div>

        <div className="relative">
          <div className="max-w-2xl">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#ddb85a]/90">
              Private by design
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Privacy is the architecture.{' '}
              <span className="font-display font-medium italic text-[#d3a641]">
                Not the fine print.
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-blue-200/70">
              Chat.co combines a protected Bedrock inference path with the controls required to
              govern public and private knowledge agents in one platform.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {pillars.map((pillar) => (
              <div
                key={pillar.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-colors duration-300 hover:border-[#d3a641]/30 hover:bg-white/[0.07]">
                <Icon icon={pillar.icon} className="text-3xl text-[#d3a641]" />
                <h3 className="mt-4 text-base font-bold text-white">{pillar.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-blue-200/60">{pillar.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 border-t border-white/10 pt-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-200/50">
              Enterprise safeguards
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {safeguards.map((safeguard) => (
                <div key={safeguard.text} className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/[0.07]">
                    <Icon icon={safeguard.icon} className="text-base text-[#d3a641]" />
                  </span>
                  <span className="text-xs font-medium leading-snug text-blue-100/80">
                    {safeguard.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default PrivateByDesign;
